import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import './JobApplicationModal.css';
import { apiCall } from '../utils/api';
import trackEvent from '../utils/analytics';

const JobApplicationModal = ({ job, onClose, onContinue, onSubmitApplication, mode = 'continue', initialFormData, quickApplyData }) => {
    const [formData, setFormData] = useState(initialFormData || {
        number: '',
        email: '',
        resumeFile: null,
        resumeFileName: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const fileInputRef = useRef(null);

    const extractResponseError = (response) => {
        const candidates = [
            response?.data?.error,
            response?.data?.message,
            response?.error,
            response?.message
        ];

        return candidates.find(Boolean) || '';
    };

    const handleResumeButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleResumeSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setError('File size should be less than 5MB');
            e.target.value = '';
            return;
        }

        setError('');
        setFormData(prev => ({ 
            ...prev, 
            resumeFile: file,
            resumeFileName: file.name 
        }));
    };

    const validate = () => {
        if (!formData.number || formData.number.length !== 10) {
            setError('Please enter a valid 10-digit phone number');
            return false;
        }
        if (!formData.email || !formData.email.includes('@')) {
            setError('Please enter a valid email');
            return false;
        }
        if (job.resume_required && !formData.resumeFile) {
            setError('Resume is required for this position');
            return false;
        }
        return true;
    };

    const handleContinueClick = (e) => {
        e.preventDefault();
        if (!validate()) return;
        handleSubmit(e);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        setError('');
        trackEvent('job_application_attempted', { job_id: job._id });

        try {
            const submitData = new FormData();
            submitData.append('number', formData.number);
            submitData.append('email', formData.email);
            if (formData.resumeFile) {
                submitData.append('resume', formData.resumeFile);
            }

            const response = await apiCall.post(`/jobs/${job._id}/apply`, submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const responseError = extractResponseError(response);
            if (responseError) {
                setError(responseError);
                return;
            }

            if (response.status) {
                trackEvent('job_application_success', { job_id: job._id });
                setSubmitted(true);
            } else {
                setError(response.message || 'Application failed. Please try again.');
            }
        } catch (err) {
            console.error('Application error:', err);
            setError('Application failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleQuickApply = async () => {
        if (!quickApplyData) return;
        setIsSubmitting(true);
        setError('');
        trackEvent('job_quick_apply_attempted', { job_id: job._id });

        try {
            const submitData = new FormData();
            submitData.append('number', quickApplyData.number);
            submitData.append('email', quickApplyData.email);
            if (quickApplyData.resumeUrl) {
                submitData.append('resume_url', quickApplyData.resumeUrl);
            }

            const response = await apiCall.post(`/jobs/${job._id}/apply`, submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const responseError = extractResponseError(response);
            if (responseError) {
                setError(responseError);
                return;
            }

            if (response.status) {
                trackEvent('job_quick_apply_success', { job_id: job._id });
                setSubmitted(true);
            } else {
                setError(response.message || 'Application failed. Please try again.');
            }
        } catch (err) {
            console.error('Quick apply error:', err);
            setError('Application failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSuccessClose = () => {
        onSubmitApplication && onSubmitApplication();
    };

    const renderForm = () => (
        <>
            <div className="jam-header">
                <h2>Apply for Articleship</h2>
                <p className="jam-firm">{job.firm_name || 'CA Firm'}</p>
            </div>

            <form className="jam-form" onSubmit={handleSubmit}>
                <div className="jam-field">
                    <label>Phone Number <span className="jam-req">*</span></label>
                    <input
                        type="tel"
                        placeholder="10-digit mobile number"
                        value={formData.number}
                        onChange={(e) => handleChange('number', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        disabled={isSubmitting}
                    />
                </div>

                <div className="jam-field">
                    <label>Email <span className="jam-req">*</span></label>
                    <input
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        disabled={isSubmitting}
                    />
                </div>

                <div className="jam-field">
                    <label>
                        Resume {job.resume_required && <span className="jam-req">*</span>}
                        {!job.resume_required && <span className="jam-optional">(Optional)</span>}
                    </label>
                    <div className="jam-resume-upload">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={handleResumeSelect}
                            disabled={isSubmitting}
                            style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden', zIndex: -1 }}
                        />
                        <button
                            type="button"
                            className="jam-upload-btn"
                            onClick={handleResumeButtonClick}
                            disabled={isSubmitting}
                        >
                            {formData.resumeFileName ? '✓ Resume Selected' : 'Upload Resume'}
                        </button>
                        {formData.resumeFileName && (
                            <span className="jam-file-name">{formData.resumeFileName}</span>
                        )}
                    </div>
                </div>

                {error && <div className="jam-error">{error}</div>}

                <button 
                    type="submit" 
                    className="jam-submit-btn"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Applying...' : 'Submit Application'}
                </button>
            </form>
        </>
    );

    const renderQuickApply = () => {
        return (
            <>
                <div className="jam-header jam-quick-header">
                    <div className="jam-quick-badge">Quick Apply</div>
                    <h2>Apply for Articleship</h2>
                    <p className="jam-firm">{job.firm_name || 'CA Firm'}</p>
                </div>

                <div className="jam-quick-details jam-quick-details-simple">
                    <div className="jam-quick-row">
                        <span className="jam-quick-label">Phone</span>
                        <span className="jam-quick-value">{quickApplyData?.number || 'Not available'}</span>
                    </div>
                    <div className="jam-quick-row">
                        <span className="jam-quick-label">Email</span>
                        <span className="jam-quick-value">{quickApplyData?.email || 'Not available'}</span>
                    </div>
                </div>

                {error && <div className="jam-error">{error}</div>}

                <div className="jam-quick-action-wrap">
                    <button
                        className="jam-submit-btn jam-quick-submit"
                        onClick={handleQuickApply}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Applying...' : 'Apply Now'}
                    </button>
                </div>
            </>
        );
    };

    const modalContent = (
        <div className="jam-overlay" onClick={submitted ? undefined : onClose}>
            <div className="jam-modal" onClick={(e) => e.stopPropagation()}>
                {!submitted && <button className="jam-close" onClick={onClose}>×</button>}
                
                {submitted ? (
                    <div className="jam-success">
                        <div className="jam-success-icon">✓</div>
                        <h2>Application Submitted!</h2>
                        <p>Your application to <strong>{job.firm_name || 'the firm'}</strong> has been sent successfully.</p>
                        <button 
                            className="jam-continue-btn"
                            onClick={handleSuccessClose}
                        >
                            Done
                        </button>
                    </div>
                ) : mode === 'quick' && quickApplyData ? (
                    renderQuickApply()
                ) : (
                    renderForm()
                )}
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

export default JobApplicationModal;
